import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, CheckCircle2, XCircle, Loader2, LogIn, ArrowRight, Gift } from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";

// Public redemption page for Pro trial links generated by admins.
// Flow:
//   1. Anyone can view /trial/:token → we fetch /api/trial/:token/info (public).
//   2. If not logged in → CTA to /auth?redirect=/trial/:token.
//   3. If logged in → auto-POST to /api/trial/:token/redeem and show the result.
export default function Trial() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  usePageSeo({
    title: "Redeem your Pro trial — Dividend Portfolio Planner",
    description: "You've been invited to try DPP Pro. Redeem your trial link to unlock the AI Portfolio Optimizer, Income Target Planner, and AI Research Chat — no credit card required.",
    path: "/trial",
    noindex: true,
  });

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoError, setInfoError] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [redeemError, setRedeemError] = useState("");

  // 1. Always fetch public info first.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/trial/${token}/info`);
        setInfo(r.data);
      } catch (e) {
        setInfoError(e?.response?.data?.detail || "Trial link is invalid or expired");
      } finally { setLoadingInfo(false); }
    })();
  }, [token]);

  // 2. Once we have a logged-in user AND a valid info payload, auto-redeem.
  useEffect(() => {
    if (authLoading) return;
    if (!user || !info || redeemResult || redeemError || redeeming) return;
    (async () => {
      setRedeeming(true);
      try {
        const r = await api.post(`/trial/${token}/redeem`);
        setRedeemResult(r.data);
        toast.success(`${r.data.days_added} days of Pro added to your account`);
      } catch (e) {
        setRedeemError(e?.response?.data?.detail || "Redemption failed");
      } finally { setRedeeming(false); }
    })();
  }, [user, info, authLoading, token, redeemResult, redeemError, redeeming]);

  return (
    <div className="max-w-lg mx-auto px-6 py-16">
      <div className="text-center mb-8">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Pro trial invite</div>
        <h1 className="serif text-4xl sm:text-5xl mt-3">Redeem your gift<span className="text-secondary">.</span></h1>
      </div>

      <Card data-testid="trial-card">
        <CardContent className="p-8">
          {redeemResult ? (
            <SuccessState result={redeemResult} note={info?.note} onDash={() => nav("/dashboard")} onBuilder={() => nav("/portfolio/new")} />
          ) : loadingInfo ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Checking link…
            </div>
          ) : redeemError ? (
            <InvalidState message={redeemError} onHome={() => nav("/")} />
          ) : infoError ? (
            <InvalidState message={infoError} onHome={() => nav("/")} />
          ) : !user ? (
            <LoggedOutState info={info} onSignIn={() => nav(`/auth?redirect=/trial/${token}`)} />
          ) : (
            <RedeemingState info={info} redeeming={redeeming} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LoggedOutState({ info, onSignIn }) {
  return (
    <div className="text-center space-y-4" data-testid="trial-logged-out">
      <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
        <Gift className="w-6 h-6" />
      </div>
      <div>
        <div className="serif text-3xl">
          <span className="text-primary">{info.days}</span> days of Pro on the house
        </div>
        {info.note && <p className="text-sm text-muted-foreground mt-2 italic">&ldquo;{info.note}&rdquo;</p>}
        <p className="text-sm text-muted-foreground mt-3">
          Sign in or create an account with DPP and this trial will be applied to your subscription automatically.
        </p>
      </div>
      <Button
        onClick={onSignIn}
        className="rounded-full"
        data-testid="trial-signin-cta"
      >
        <LogIn className="w-4 h-4 mr-2" /> Sign in to claim
      </Button>
      <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
        Link valid until {info.expires_at ? new Date(info.expires_at).toLocaleDateString() : "further notice"}
      </p>
    </div>
  );
}

function RedeemingState({ info, redeeming }) {
  return (
    <div className="text-center space-y-3" data-testid="trial-redeeming">
      <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
      <div className="serif text-2xl">Applying {info.days} days of Pro…</div>
      <p className="text-xs text-muted-foreground">Hang tight — {redeeming ? "we're setting things up." : "starting soon."}</p>
    </div>
  );
}

function SuccessState({ result, note, onDash, onBuilder }) {
  const expiry = result.new_expiry ? new Date(result.new_expiry).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  return (
    <div className="text-center space-y-4" data-testid="trial-success">
      <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
        <CheckCircle2 className="w-7 h-7" />
      </div>
      <div>
        <div className="serif text-3xl text-primary">Pro unlocked</div>
        <p className="text-sm text-foreground/85 mt-2">
          You&apos;ve been credited <strong>{result.days_added} days</strong> of Pro. Your access runs through <strong>{expiry}</strong>.
        </p>
        {note && <p className="text-xs text-muted-foreground mt-2 italic">&ldquo;{note}&rdquo;</p>}
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2">
        <Button variant="outline" onClick={onDash} className="rounded-full" data-testid="trial-goto-dashboard">
          Dashboard
        </Button>
        <Button onClick={onBuilder} className="rounded-full" data-testid="trial-goto-builder">
          Build a portfolio <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
      <p className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center justify-center gap-1.5 pt-2">
        <Sparkles className="w-3 h-3" /> AI Optimizer · Income Target Planner · Ticker Research
      </p>
    </div>
  );
}

function InvalidState({ message, onHome }) {
  return (
    <div className="text-center space-y-4" data-testid="trial-invalid">
      <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive grid place-items-center mx-auto">
        <XCircle className="w-7 h-7" />
      </div>
      <div>
        <div className="serif text-2xl">This link can&apos;t be redeemed</div>
        <p className="text-sm text-muted-foreground mt-2">{message}</p>
        <p className="text-xs text-muted-foreground mt-1">
          Ask whoever sent you the link for a fresh one, or explore DPP for free — no card needed.
        </p>
      </div>
      <Button onClick={onHome} variant="outline" className="rounded-full">Go to DPP</Button>
    </div>
  );
}
